import React from 'react';

import { useIntl } from 'react-intl';

import { UploadOutlined } from '@ant-design/icons';
import { Button, Drawer, Form, Grid, Input, Modal, Space, Spin, Typography, Upload } from 'antd';

import {
  OPTION_LIST_LABEL_MAX_LENGTH,
  OPTION_LIST_MAX_ITEMS,
  OPTION_LIST_NAME_MAX_LENGTH,
} from '@src/packages/optionLists/constants';
import type OptionListService from '@src/services/OptionListService';

import { useOptionListForm } from './useOptionListForm';

import styles from './styles.module.less';

interface OptionListFormProps {
  organizationId: string;
  /** Null to create a new list. */
  listId: string | null;
  /** Owning division of a list being created (form builder, division roles); omit for org-wide. */
  divisionId?: string | null;
  service: OptionListService;
  onClose: (saved: boolean, savedListId?: string) => void;
  /**
   * `modal` when opened from inside another drawer (the form editor): the design system does not
   * nest drawers.
   */
  container?: 'drawer' | 'modal';
}

/**
 * Create or edit an option list. Items are edited as text, one per line; pasting or importing a
 * CSV (first column) fills the same box. Removing a line deactivates that item.
 */
const OptionListForm: React.FC<OptionListFormProps> = ({
  organizationId,
  listId,
  divisionId,
  service,
  onClose,
  container = 'drawer',
}) => {
  const intl = useIntl();
  const isMobile = !Grid.useBreakpoint().md;
  const { form, loading, saving, itemCount, onSubmit, importFile, validateItems } = useOptionListForm({
    organizationId,
    listId,
    divisionId,
    service,
    onClose,
  });

  const title = intl.formatMessage({ id: listId ? 'optionLists.form.edit.title' : 'optionLists.form.create.title' });
  const submitLabel = intl.formatMessage({ id: listId ? 'optionLists.form.submit.edit' : 'optionLists.form.submit.create' });
  const cancelLabel = intl.formatMessage({ id: 'button.cancel' });
  const cancel = () => onClose(false);

  const footer = isMobile ? (
    <Space className={styles.footerMobile} direction="vertical">
      <Button block disabled={loading} loading={saving} onClick={form.submit} size="large" type="primary">
        {submitLabel}
      </Button>
      <Button block onClick={cancel} size="large">
        {cancelLabel}
      </Button>
    </Space>
  ) : (
    <Space className={styles.footer}>
      <Button onClick={cancel}>{cancelLabel}</Button>
      <Button disabled={loading} loading={saving} onClick={form.submit} type="primary">
        {submitLabel}
      </Button>
    </Space>
  );

  const content = (
    <Spin spinning={loading}>
      <Form colon={false} form={form} layout="vertical" name="optionList" onFinish={onSubmit} size="middle">
        <Form.Item
          label={intl.formatMessage({ id: 'optionLists.form.name' })}
          name="name"
          rules={[
            { required: true, whitespace: true, message: intl.formatMessage({ id: 'optionLists.form.name.required' }) },
            { max: OPTION_LIST_NAME_MAX_LENGTH },
          ]}
        >
          <Input placeholder={intl.formatMessage({ id: 'optionLists.form.name.placeholder' })} />
        </Form.Item>
        <Form.Item
          className={styles.itemsField}
          extra={intl.formatMessage(
            { id: 'optionLists.form.items.help' },
            { max: OPTION_LIST_MAX_ITEMS, maxLength: OPTION_LIST_LABEL_MAX_LENGTH },
          )}
          label={intl.formatMessage({ id: 'optionLists.form.items' })}
          name="items"
          required
          rules={[{ validator: validateItems }]}
        >
          <Input.TextArea
            autoSize={{ minRows: 8, maxRows: 16 }}
            placeholder={intl.formatMessage({ id: 'optionLists.form.items.placeholder' })}
          />
        </Form.Item>
        <div className={styles.itemsActions}>
          <Typography.Text type="secondary">
            {intl.formatMessage({ id: 'optionLists.form.items.count' }, { count: itemCount })}
          </Typography.Text>
          <Upload accept=".csv,.txt" beforeUpload={importFile} showUploadList={false}>
            <Button icon={<UploadOutlined />}>{intl.formatMessage({ id: 'optionLists.form.import' })}</Button>
          </Upload>
        </div>
      </Form>
    </Spin>
  );

  if (container === 'modal') {
    return (
      <Modal
        destroyOnClose
        footer={footer}
        maskClosable={false}
        onCancel={cancel}
        open
        title={title}
        width={isMobile ? '100%' : 520}
      >
        {content}
      </Modal>
    );
  }

  return (
    <Drawer destroyOnClose footer={footer} onClose={cancel} open title={title} width={isMobile ? '100%' : 480}>
      {content}
    </Drawer>
  );
};

export default OptionListForm;
